import { useState } from 'react';
import { SectionCard } from './SectionCard';
import { ExerciseForm } from './ExerciseForm';
import { ExerciseList } from './ExerciseList';
import { ExerciseMediaManager } from './ExerciseMediaManager';
import { DraftRecoveryBanner } from './DraftRecoveryBanner';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Panel } from '../ui/Panel';
import { Button } from '../ui/Button';
import { ALERTS, BORDERS } from '../../styles/classNames';
import type { Exercise, Media } from '../../types';
import type { UploadingMedia } from '../../hooks/useMediaUpload';

interface ExerciseLibraryViewProps {
  // Data
  filteredExercises: Exercise[];
  exerciseSearch: string;
  exerciseForm: any;
  exerciseMedia: Media[];
  uploads: UploadingMedia[];
  isUploading: boolean;
  selectedExerciseId: string | null;
  error: string | null;

  // Draft recovery
  hasDraft: boolean;
  draftMediaCount?: number;
  draftHasFormData?: boolean;

  // Actions
  onSearchChange: (search: string) => void;
  onExerciseFormChange: (form: any) => void;
  onEditExercise: (exercise: Exercise) => void;
  onDeleteExercise: (id: string) => void;
  onSaveExercise: () => void;
  onResetDraft: (clearDraft?: boolean) => void;
  onAddMedia: (files: FileList | null) => void;
  onAddExternalMedia: (url: string) => void;
  onRemoveMedia: (mediaId: string) => Promise<void>;
  onRetryUpload?: (id: string) => void;
  onRemoveUpload?: (id: string) => void;
  onRecoverDraft?: () => void;
  onDiscardDraft?: () => void;
}

export function ExerciseLibraryView({
  filteredExercises,
  exerciseSearch,
  exerciseForm,
  exerciseMedia,
  uploads,
  isUploading,
  selectedExerciseId,
  error,
  hasDraft,
  draftMediaCount = 0,
  draftHasFormData = false,
  onSearchChange,
  onExerciseFormChange,
  onEditExercise,
  onDeleteExercise,
  onSaveExercise,
  onResetDraft,
  onAddMedia,
  onAddExternalMedia,
  onRemoveMedia,
  onRetryUpload,
  onRemoveUpload,
  onRecoverDraft,
  onDiscardDraft,
}: ExerciseLibraryViewProps) {
  const [isExerciseModalOpen, setIsExerciseModalOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Check if form has any meaningful content entered
  const hasFormContent = Boolean(
    exerciseForm.name?.trim() ||
    exerciseForm.description?.trim() ||
    exerciseForm.howTo?.trim() ||
    exerciseForm.defaultCues?.trim() ||
    exerciseForm.movementPattern ||
    (exerciseForm.equipmentTags && exerciseForm.equipmentTags.length > 0) ||
    (exerciseForm.targetMuscleGroups && exerciseForm.targetMuscleGroups.length > 0) ||
    exerciseForm.defaultSets !== 3 ||
    exerciseForm.defaultReps !== 8 ||
    exerciseForm.defaultRestSec !== 90
  );

  // Check if there's anything unsaved (for new exercises only)
  const hasUnsavedContent = !selectedExerciseId && (hasFormContent || exerciseMedia.length > 0);

  const handleOpenExerciseModal = () => {
    // If there's a draft, open modal with draft content (already loaded)
    // Otherwise reset and start fresh
    if (!hasDraft) {
      onResetDraft(false);
    }
    setIsExerciseModalOpen(true);
  };

  const handleEditExercise = (exercise: Exercise) => {
    onEditExercise(exercise);
    setIsExerciseModalOpen(true);
  };

  const handleSaveExercise = () => {
    onSaveExercise();
    setIsExerciseModalOpen(false);
  };

  const handleCancelExercise = () => {
    // If there's unsaved content (text or media) for new exercise, show confirmation
    if (hasUnsavedContent) {
      setConfirmDiscard(true);
      return;
    }
    onResetDraft(false);
    setIsExerciseModalOpen(false);
  };

  const handleConfirmDiscard = () => {
    onResetDraft(true); // Clear draft and delete storage files
    setConfirmDiscard(false);
    setIsExerciseModalOpen(false);
  };

  const handleSaveDraft = () => {
    // Draft is already auto-saved, just close the modal
    setConfirmDiscard(false);
    setIsExerciseModalOpen(false);
  };

  const handleRecoverDraft = () => {
    onRecoverDraft?.();
    setIsExerciseModalOpen(true);
  };

  const handleDiscardDraft = () => {
    onDiscardDraft?.();
  };

  const handleDeleteExercise = () => {
    if (selectedExerciseId) {
      onDeleteExercise(selectedExerciseId);
      setConfirmDelete(false);
      setIsExerciseModalOpen(false);
    }
  };

  return (
    <>
      <SectionCard
        eyebrow="Exercise Management"
        title="Exercise Library"
        fillHeight
        action={
          <Button onClick={handleOpenExerciseModal} size="sm" data-testid="add-exercise-button">
            + Add Exercise
          </Button>
        }
      >
        {/* Draft Recovery Banner */}
        {!isExerciseModalOpen && (
          <DraftRecoveryBanner
            isVisible={hasDraft}
            mediaCount={draftMediaCount}
            hasFormData={draftHasFormData}
            onRecover={handleRecoverDraft}
            onDiscard={handleDiscardDraft}
          />
        )}

        {error && (
          <div className={`mb-4 ${ALERTS.error} shrink-0`}>
            {error}
          </div>
        )}
        <ExerciseList
          exercises={filteredExercises}
          search={exerciseSearch}
          onSearchChange={onSearchChange}
          onEdit={handleEditExercise}
        />
      </SectionCard>

      {/* Exercise Panel */}
      <Panel
        isOpen={isExerciseModalOpen}
        onClose={handleCancelExercise}
        title={selectedExerciseId ? 'Edit Exercise' : 'Add Exercise'}
        variant="modal"
        size="lg"
      >
        <div className="space-y-6">
          {/* Exercise Details */}
          <div>
            <ExerciseForm
              form={exerciseForm}
              onChange={onExerciseFormChange}
              onSubmit={handleSaveExercise}
              mode={selectedExerciseId ? 'edit' : 'create'}
              onCancelEdit={handleCancelExercise}
              hideActions={true}
            />
          </div>

          {/* Media Section */}
          <div className={`pt-6 ${BORDERS.top}`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
              Media & Demonstrations
            </label>
            <ExerciseMediaManager
              media={exerciseMedia}
              uploads={uploads}
              isUploading={isUploading}
              onAddFiles={onAddMedia}
              onRemove={onRemoveMedia}
              onAddExternal={onAddExternalMedia}
              onRetry={onRetryUpload}
              onRemoveUpload={onRemoveUpload}
            />
          </div>

          {/* Save Button at Bottom */}
          <div className={`pt-6 ${BORDERS.top}`}>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleSaveExercise();
              }}
              data-testid="exercise-save-button"
              disabled={isUploading}
              className="w-full"
              size="lg"
            >
              {isUploading ? 'Uploading media...' : selectedExerciseId ? 'Save Changes' : 'Save to Library'}
            </Button>
            {selectedExerciseId && (
              <>
                <Button
                  onClick={handleCancelExercise}
                  variant="secondary"
                  className="w-full mt-2"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => setConfirmDelete(true)}
                  variant="danger"
                  className="w-full mt-6"
                >
                  Delete Exercise
                </Button>
              </>
            )}
          </div>
        </div>
      </Panel>

      {/* Discard confirmation dialog */}
      <ConfirmDialog
        isOpen={confirmDiscard}
        title="You have unsaved changes"
        message="Would you like to save this as a draft to continue later, or discard your changes?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tertiaryLabel="Save draft"
        variant="warning"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setConfirmDiscard(false)}
        onTertiary={handleSaveDraft}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete exercise?"
        message="This will permanently delete this exercise from your library. This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleDeleteExercise}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
